import {
  getCidades,
  getExperiencias,
  saveExperiencia,
  updateCidade,
  type ExperienciaAdmin,
} from './experienciasStorage';
import { uploadAdminMedia } from './mediaUploadApi';

/** Alinhado com ExperienciasPage: máximo de fotos por experiência. */
const MAX_IMAGENS_EXPERIENCIA = 8;

let toS3Counter = 0;

/**
 * Converte data URL ou URL http(s) em referência s3: via POST /api/admin/media/upload.
 * URLs externas podem falhar por CORS no browser.
 */
async function toS3Reference(stored: string, category: 'experiencias' | 'cidades'): Promise<string> {
  const idx = toS3Counter++;
  if (stored.startsWith('s3:')) return stored;
  if (stored.startsWith('data:')) {
    const blob = await (await fetch(stored)).blob();
    const file = new File([blob], `mig-${category}-${idx}.jpg`, { type: blob.type || 'image/jpeg' });
    const { reference } = await uploadAdminMedia(file, category);
    return reference;
  }
  const res = await fetch(stored, { mode: 'cors' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const file = new File([blob], `mig-${category}-${idx}.jpg`, { type: blob.type || 'image/jpeg' });
  const { reference } = await uploadAdminMedia(file, category);
  return reference;
}

/**
 * Migra `imagens[]` e `imagensSEO` de todas as experiências: substitui data URLs e URLs http por `s3:chave`.
 * Mantém entradas que já são s3: ou que falharem (reverte para o valor original naquele slot).
 */
export async function migrateAllExperienciaImagesToCloud(
  onProgress?: (detail: string) => void
): Promise<{ experienciasAtualizadas: number; imagensMigradas: number; erros: number }> {
  const list = getExperiencias();
  let experienciasAtualizadas = 0;
  let imagensMigradas = 0;
  let erros = 0;

  for (const exp of list) {
    const raw = exp.imagens;
    const hasLegacyImagens = !!(raw?.length);
    const seo = exp.imagensSEO;
    const hasSeo = !!(seo?.length);
    if (!hasLegacyImagens && !hasSeo) continue;

    let changed = false;
    let next: string[] | undefined;
    if (hasLegacyImagens) {
      const trimmed = raw!.length > MAX_IMAGENS_EXPERIENCIA;
      if (trimmed) {
        onProgress?.(`${exp.titulo || exp.id}: mantendo só as primeiras ${MAX_IMAGENS_EXPERIENCIA} fotos (legado).`);
      }
      const imgs = raw!.slice(0, MAX_IMAGENS_EXPERIENCIA);
      next = [];
      for (let i = 0; i < imgs.length; i++) {
        const url = imgs[i];
        if (url.startsWith('s3:')) {
          next.push(url);
          continue;
        }
        onProgress?.(`${exp.titulo || exp.id} — imagem legada ${i + 1}/${imgs.length}`);
        try {
          const ref = await toS3Reference(url, 'experiencias');
          next.push(ref);
          if (ref !== url) {
            imagensMigradas++;
            changed = true;
          }
        } catch {
          erros++;
          next.push(url);
        }
      }
    }

    let nextSeo = seo;
    if (hasSeo) {
      nextSeo = [...seo!];
      for (let i = 0; i < nextSeo.length; i++) {
        const ent = nextSeo[i]!;
        const url = ent.url;
        if (url.startsWith('s3:')) continue;
        onProgress?.(`${exp.titulo || exp.id} — SEO ${i + 1}/${nextSeo.length}`);
        try {
          const ref = await toS3Reference(url, 'experiencias');
          nextSeo[i] = { ...ent, url: ref };
          if (ref !== url) {
            imagensMigradas++;
            changed = true;
          }
        } catch {
          erros++;
          nextSeo[i] = ent;
        }
      }
    }

    if (changed || (hasLegacyImagens && raw!.length > MAX_IMAGENS_EXPERIENCIA)) {
      saveExperiencia({
        ...exp,
        ...(next ? { imagens: next } : {}),
        ...(nextSeo ? { imagensSEO: nextSeo } : {}),
      } as ExperienciaAdmin);
      experienciasAtualizadas++;
    }
  }

  return { experienciasAtualizadas, imagensMigradas, erros };
}

/**
 * Migra fotos das cidades (URLs / data URL) para referências s3: na API configurada (local ou VITE_ADMIN_SYNC_URL).
 */
export async function migrateAllCidadeFotosToCloud(
  onProgress?: (detail: string) => void
): Promise<{ cidadesAtualizadas: number; fotosMigradas: number; erros: number }> {
  const list = getCidades();
  let cidadesAtualizadas = 0;
  let fotosMigradas = 0;
  let erros = 0;

  for (const c of list) {
    const fotos = c.fotos;
    if (!fotos?.length) continue;
    let changed = false;
    const next: string[] = [];
    for (let i = 0; i < fotos.length; i++) {
      const url = fotos[i]!;
      if (url.startsWith('s3:')) {
        next.push(url);
        continue;
      }
      onProgress?.(`${c.cidade || c.id} — foto ${i + 1}/${fotos.length}`);
      try {
        const ref = await toS3Reference(url, 'cidades');
        next.push(ref);
        if (ref !== url) {
          fotosMigradas++;
          changed = true;
        }
      } catch {
        erros++;
        next.push(url);
      }
    }
    if (changed) {
      updateCidade(c.id, { fotos: next, fotoPrincipalIndex: c.fotoPrincipalIndex });
      cidadesAtualizadas++;
    }
  }

  return { cidadesAtualizadas, fotosMigradas, erros };
}
